import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { AdminUser, Page } from '../../lib/types';
import { titleCase } from '../../lib/format';
import {
  Button,
  Checkbox,
  Dialog,
  ErrorNotice,
  Field,
  Input,
  Select,
  Textarea,
  useToast,
} from '../../components/ui';

/**
 * §6.3 — put someone on an account.
 *
 * Temporary coverage must be date-bounded: the API refuses it without an end
 * date, because coverage that never expires is a permanent grant recorded under
 * a misleading name — and §62 tests that an expired window stops granting
 * access.
 */
export function AssignOwnerDialog({ accountId, onClose }: { accountId: string; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [userId, setUserId] = useState('');
  const [assignmentType, setAssignmentType] = useState('secondary_csm');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [operationalAccess, setOperationalAccess] = useState(true);
  const [notes, setNotes] = useState('');

  const users = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api.get<Page<AdminUser>>('/admin/users'),
  });

  const temporary = assignmentType === 'temporary_coverage';

  const create = useMutation({
    mutationFn: () =>
      api.post(`/accounts/${accountId}/assignments`, {
        userId,
        assignmentType,
        startsAt: startsAt || undefined,
        endsAt: endsAt || null,
        operationalAccess,
        notes: notes || null,
      }),
    onSuccess: () => {
      toast('Assignment created');
      void queryClient.invalidateQueries({ queryKey: ['account-summary', accountId] });
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-ownership'] });
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Assign someone to this account"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={create.isPending || !userId || (temporary && !endsAt)}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Saving…' : 'Assign'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {create.error && <ErrorNotice error={create.error} />}

        <Field label="Person" required>
          <Select value={userId} onChange={(event) => setUserId(event.target.value)}>
            <option value="">Select a person…</option>
            {(users.data?.data ?? [])
              .filter((u) => u.status === 'active')
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.fullName}
                  {u.title ? ` — ${u.title}` : ''}
                </option>
              ))}
          </Select>
        </Field>

        <Field label="Assignment type" required>
          <Select value={assignmentType} onChange={(event) => setAssignmentType(event.target.value)}>
            {[
              'primary_csm',
              'secondary_csm',
              'executive_sponsor',
              'temporary_coverage',
              'collaborator',
            ].map((t) => (
              <option key={t} value={t}>
                {titleCase(t)}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Starts" hint="Defaults to now.">
            <Input
              type="date"
              value={startsAt}
              onChange={(event) => setStartsAt(event.target.value)}
            />
          </Field>
          <Field
            label="Ends"
            required={temporary}
            hint={temporary ? 'Required for temporary coverage.' : 'Optional.'}
          >
            <Input type="date" value={endsAt} onChange={(event) => setEndsAt(event.target.value)} />
          </Field>
        </div>

        <Checkbox
          checked={operationalAccess}
          label="Grants operational access to this account"
          onChange={(event) => setOperationalAccess(event.target.checked)}
        />

        <Field label="Notes">
          <Textarea rows={2} value={notes} onChange={(event) => setNotes(event.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}